import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    ProgressBar, Tooltip, OverlayTrigger, InputGroup, Form
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function EvaluationFournisseurs() {
    const navigate = useNavigate();
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date_evaluation', direction: 'desc' });

    useEffect(() => {
        fetchEvaluations();
        fetchStats();
    }, []);

    const fetchEvaluations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/fournisseurs.php?action=evaluations&limit=100',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setEvaluations(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/fournisseurs.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getNoteStars = (note) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`ri-star-${i <= note ? 'fill' : 'line'} text-warning`} 
                   style={{ fontSize: '1.1rem', marginRight: '2px' }}></i>
            );
        }
        return stars;
    };

    const getNoteColor = (note) => {
        if (note >= 4.5) return 'success';
        if (note >= 4) return 'success';
        if (note >= 3) return 'warning';
        if (note >= 2) return 'danger';
        return 'danger';
    };

    const getNoteBadge = (note) => {
        if (note >= 4.5) return { bg: 'success', label: 'Excellent' };
        if (note >= 4) return { bg: 'success', label: 'Très bon' };
        if (note >= 3) return { bg: 'warning', label: 'Bon' };
        if (note >= 2) return { bg: 'danger', label: 'Médiocre' };
        return { bg: 'danger', label: 'Mauvais' };
    };

    const getCriterionIcon = (value) => {
        if (value >= 4) return 'ri-emotion-happy-line text-success';
        if (value >= 3) return 'ri-emotion-normal-line text-warning';
        return 'ri-emotion-unhappy-line text-danger';
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)} 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredEvaluations = evaluations
        .filter(e => 
            e.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
            e.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase()) ||
            e.commentaire?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'date_evaluation') {
                return sortConfig.direction === 'asc' 
                    ? new Date(a.date_evaluation) - new Date(b.date_evaluation)
                    : new Date(b.date_evaluation) - new Date(a.date_evaluation);
            }
            if (sortConfig.key === 'note') {
                return sortConfig.direction === 'asc' 
                    ? (a.note || 0) - (b.note || 0)
                    : (b.note || 0) - (a.note || 0);
            }
            if (sortConfig.key === 'fournisseur_nom') {
                return sortConfig.direction === 'asc'
                    ? a.fournisseur_nom?.localeCompare(b.fournisseur_nom)
                    : b.fournisseur_nom?.localeCompare(a.fournisseur_nom);
            }
            return 0;
        });

    const moyenneGenerale = evaluations.length > 0 
        ? (evaluations.reduce((acc, e) => acc + (e.note || 0), 0) / evaluations.length).toFixed(1)
        : 0;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des évaluations...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchEvaluations}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête avec design moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))',
                                            boxShadow: '0 10px 20px rgba(245, 158, 11, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-star-line text-warning" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Évaluation des fournisseurs
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {evaluations.length} évaluation(s) • Note moyenne: {moyenneGenerale}/5
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/achats/fournisseurs')}
                                    className="rounded-pill px-4 py-2 d-flex align-items-center gap-2"
                                >
                                    <i className="ri-arrow-left-line"></i>
                                    <span>Retour</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-star-line text-warning fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Note moyenne</small>
                                                <h3 className="mb-0 fw-bold text-warning">{stats.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}</h3>
                                                <small className="text-muted">/5</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-checkbox-circle-line text-success fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Fournisseurs évalués</small>
                                                <h3 className="mb-0 fw-bold text-success">
                                                    {new Set(evaluations.map(e => e.id_fournisseur)).size}
                                                </h3>
                                                <small className="text-muted">sur {stats.total_fournisseurs || 0}</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-calendar-line text-info fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Dernière évaluation</small>
                                                <h3 className="mb-0 fw-bold text-info">
                                                    {evaluations[0] ? formatDate(evaluations[0].date_evaluation) : '-'}
                                                </h3>
                                                <small className="text-muted">la plus récente</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher par fournisseur, code, commentaire..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end align-items-center">
                                    <span className="badge-count bg-primary text-white px-3 py-2 rounded-pill">
                                        {filteredEvaluations.length} évaluation(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des évaluations */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <SortableHeader field="fournisseur_nom">Fournisseur</SortableHeader>
                                            <SortableHeader field="date_evaluation">Date</SortableHeader>
                                            <SortableHeader field="note">Note</SortableHeader>
                                            <th>Délais</th>
                                            <th>Qualité</th>
                                            <th>Prix</th>
                                            <th>Comment.</th>
                                            <th>Évalua.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEvaluations.length > 0 ? (
                                            filteredEvaluations.map((e, index) => {
                                                const noteBadge = getNoteBadge(e.note);
                                                return (
                                                    <tr key={e.id_evaluation} className="evaluation-row" style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" 
                                                                     style={{ width: '36px', height: '36px' }}>
                                                                    <i className="ri-truck-line text-primary"></i>
                                                                </div>
                                                                <div>
                                                                    <Link to={`/achats/fournisseurs/${e.id_fournisseur}`} className="text-primary fw-semibold text-decoration-none">
                                                                        {e.fournisseur_nom}
                                                                    </Link>
                                                                    <div><small className="text-muted">{e.code_fournisseur}</small></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="ri-calendar-line text-muted me-2"></i>
                                                                <span>{formatDate(e.date_evaluation)}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge 
                                                                    bg={noteBadge.bg} 
                                                                    className="px-3 py-2 rounded-pill"
                                                                    style={{
                                                                        minWidth: '70px',
                                                                        background: `linear-gradient(135deg, var(--bs-${noteBadge.bg}), var(--bs-${noteBadge.bg}-dark))`,
                                                                        border: 'none'
                                                                    }}
                                                                >
                                                                    {e.note}/5 • {noteBadge.label}
                                                                </Badge>
                                                                {getNoteStars(e.note)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Délais: {e.critere_delai}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_delai)}></i>
                                                                    <ProgressBar 
                                                                        now={e.critere_delai * 20} 
                                                                        style={{ width: '60px', height: '6px' }}
                                                                        variant={e.critere_delai >= 4 ? 'success' : e.critere_delai >= 3 ? 'warning' : 'danger'}
                                                                    />
                                                                </div>
                                                            </OverlayTrigger>
                                                        </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Qualité: {e.critere_qualite}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_qualite)}></i>
                                                                    <ProgressBar 
                                                                        now={e.critere_qualite * 20} 
                                                                        style={{ width: '60px', height: '6px' }}
                                                                        variant={e.critere_qualite >= 4 ? 'success' : e.critere_qualite >= 3 ? 'warning' : 'danger'}
                                                                    />
                                                                </div>
                                                            </OverlayTrigger>
                                                        </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Prix: {e.critere_prix}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_prix)}></i>
                                                                    <ProgressBar 
                                                                        now={e.critere_prix * 20} 
                                                                        style={{ width: '60px', height: '6px' }}
                                                                        variant={e.critere_prix >= 4 ? 'success' : e.critere_prix >= 3 ? 'warning' : 'danger'}
                                                                    />
                                                                </div>
                                                            </OverlayTrigger>
                                                        </td>
                                                        <td>
                                                            {e.commentaire ? (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>{e.commentaire}</Tooltip>}
                                                                >
                                                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                                                        <i className="ri-chat-1-line text-muted me-1"></i>
                                                                        {e.commentaire}
                                                                    </span>
                                                                </OverlayTrigger>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="ri-user-line text-muted me-2"></i>
                                                                <span>{e.prenom_utilisateur} {e.nom_utilisateur}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-star-line empty-icon text-warning" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune évaluation trouvée</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par évaluer vos fournisseurs"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/fournisseurs')}
                                                            >
                                                                <i className="ri-arrow-left-line me-2"></i>Voir les fournisseurs
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .evaluation-row {
                        animation: fadeIn 0.3s ease;
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .progress {
                        border-radius: 10px;
                        background-color: #e9ecef;
                    }

                    .badge-count {
                        font-size: 0.9rem;
                        font-weight: 500;
                    }

                    .empty-state {
                        padding: 3rem 1rem;
                    }

                    .empty-icon {
                        opacity: 0.5;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}